import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  AlertOctagon, ArrowRight, Loader2, RefreshCw, Sparkles, Zap,
} from "lucide-react";

const PRIORITY = {
  urgent:  { color: "#F87171", label: "URGENT",  icon: AlertOctagon },
  routine: { color: "#60A5FA", label: "ROUTINE", icon: ArrowRight },
  monitor: { color: "#4ADE80", label: "MONITOR", icon: Sparkles },
};

/**
 * DiagnosticsSuggestion — Phase 2.
 *
 * Med Nexus AI reads the patient's specialty facts + last few uploaded
 * report summaries and suggests the single highest-yield next diagnostic
 * step. Cache-served per-day (never regenerates on every render).
 *
 * Props:
 *   - specialty: 'cardiology' | 'neurology' | 'oncology' | 'nephrology'
 */
export default function DiagnosticsSuggestion({ specialty }) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    try {
      const { data } = await api.get(
        `/ai/diagnostics-suggestion?specialty=${specialty}${refresh ? "&refresh=1" : ""}`,
      );
      setState({ loading: false, data, error: "" });
    } catch (e) {
      setState({ loading: false, data: null, error: e?.response?.data?.detail || "Failed to load AI diagnostics suggestion" });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); /* on mount + specialty change */ }, [specialty]);

  if (state.loading) {
    return (
      <div className="ros-card flex items-center gap-2 text-sm text-[#A3ACA7]"
           data-testid={`diagnostics-loading-${specialty}`}>
        <Loader2 size={14} className="animate-spin" /> Med Nexus AI analyzing next workup…
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="ros-card text-sm text-[#F87171]" data-testid={`diagnostics-error-${specialty}`}>
        {state.error || "No suggestion available."}
      </div>
    );
  }

  const s = state.data.suggestion || {};
  const p = PRIORITY[s.priority] || PRIORITY.routine;
  const Icon = p.icon;
  return (
    <div className="ros-card" data-testid={`diagnostics-suggestion-${specialty}`}
         style={{
           borderLeftWidth: 2, borderLeftColor: p.color,
           background: `${p.color}08`,
         }}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-[#A78BFA]" />
          <div className="ros-label" style={{ color: p.color }}>
            Med Nexus AI · suggested next workup
          </div>
          {state.data.stale && (
            <span className="text-[9px] text-[#FBBF24]">· stale</span>
          )}
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
                data-testid={`diagnostics-refresh-${specialty}`}
                className="text-[10px] text-[#A3ACA7] hover:text-white flex items-center gap-1 disabled:opacity-50">
          <RefreshCw size={10} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
             style={{ background: `${p.color}22`, border: `1px solid ${p.color}55` }}>
          <Icon size={14} style={{ color: p.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ color: p.color, background: `${p.color}22`, border: `1px solid ${p.color}55` }}>
              {p.label}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-[#7A8480]">
              confidence: {s.confidence || "medium"}
            </span>
          </div>
          <div className="text-base font-semibold text-white mt-2 leading-snug"
               data-testid={`diagnostics-next-step-${specialty}`}>
            {s.next_step}
          </div>
          {s.rationale && (
            <div className="mt-2 text-xs text-[#D1D5DB] leading-relaxed">
              <span className="text-[#A3ACA7] uppercase tracking-wider text-[10px] mr-1">Why:</span>
              {s.rationale}
            </div>
          )}
          {Array.isArray(s.alternatives) && s.alternatives.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-1">Alternatives</div>
              <div className="flex flex-wrap gap-1.5">
                {s.alternatives.map((a, i) => (
                  <span key={i} className="text-[11px] text-[#D1D5DB] bg-[#1C201E] border border-[#272E2B] px-2 py-0.5 rounded-full">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
          {s.watch_for && (
            <div className="mt-3 text-xs p-2.5 rounded-lg"
                 style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)" }}>
              <span className="text-[#F87171] font-semibold uppercase text-[10px] tracking-wider mr-2">Watch for</span>
              <span className="text-[#F87171]">{s.watch_for}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
